/* eslint-disable camelcase */
import { useEffect, useRef, useState } from 'react';
import styles from './styles';
import { Box, Grid, IconButton, TextField, Typography } from '@mui/material';
import Datagrid from '../../Datagrid';
import PropTypes from 'prop-types';
import { AddRounded, DeleteRounded, RemoveRounded, SaveRounded } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import { parametriTablecColumns } from './table_config';
import { APP_ACTION_TYPES, HYDRAULICS_RUN_DETAILS, MAX_ROW_LIMITS, PARAMETER_ANALYSIS_TYPE, PARAMETER_ANALYSIS_TYPE_VALUE } from '../../../../utils/constants';
import SelectEditValueTypes from '../../SelectEditValueTypes';
import { addNewParameterRow, createHydraulicParameter, deleteHydraulicParameter, listHydraulicsParameter, updateHydraulicParameter } from '../../../../store/actions/hydraulicsParameterAnalysis';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import { UNIT_SYSTEM } from '../../../../shared/config/hydraulics_constants';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import { COLORS } from '../../../../shared/config/colors';
import { listHydraulicsDetails } from '../../../../store/actions/hydraulics';
import { checkPermissions } from '../../../../utils/checkPermissions';

const ParametricAnalysisModal = ({
  selectedJob,
  rowData,
  setHydraulicsParameterData,
  proxyLoader,
  setAccordionProperty,
  saveHydraulicDetails
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { unit, convertUnitSystem } = useUnitConversion();
  const hydraulics = useSelector((state) => state.hydraulics);
  const hydraulicsParameter = useSelector((state) => state.hydraulicsParameterAnalysis);
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();

  useEffect(() => {
    if (!hydraulicsParameter.loading) {
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData?._id
      };
      dispatch(listHydraulicsParameter(payload));
    }
  }, []);

  useEffect(() => {
    if (hydraulicsParameter?.inputs) {
      const resultArray = (hydraulicsParameter?.inputs || []).map((item) => {
        const matchedKey = Object.keys(PARAMETER_ANALYSIS_TYPE_VALUE)
          .find(key => PARAMETER_ANALYSIS_TYPE_VALUE[key] === item.parameter) || item.parameter;

        const decimalPlaces = hydraulicsParameter?.units?.inputs?.[matchedKey]?.decimals ?? 2;
        return {
          ...item,
          parameter: PARAMETER_ANALYSIS_TYPE_VALUE[matchedKey] || matchedKey,
          values: item.values?.map(val => {
            if (val === '' || val === null || val === undefined) return val;
            const numericValue = Number(val);
            if (isNaN(numericValue)) return val;

            return parseFloat(numericValue.toFixed(decimalPlaces));
          }) || [],
          _id: item._id,
        };
      });
      setProperties(resultArray);
      setAccordionProperty(resultArray);
      setHydraulicsParameterData(resultArray);
    }
  }, [hydraulicsParameter]);

  useEffect(() => {
    const lastIndex = properties.length - 1;
    const lastRow = properties[lastIndex];
    if (lastRow?.parameter === '') {
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: lastIndex
        });
      }, 50);
    }
  }, [properties?.length]);

  const handleAddParameter = () => {
    if (properties[0]?.isNewRow) return;
    dispatch(addNewParameterRow());
  };

  const updateData = (id, field, value) => {
    setProperties((prevProperties) => {
      const existingParameter = prevProperties.find(
        (item) => item.parameter === value && item._id !== id
      );
      if (field === 'parameter' && existingParameter) {
        dispatch(
          enqueueSnackbar(
          `${existingParameter.parameter} already selected.`,
          'warning',
          new Date().getTime() + Math.random()
          )
        );
        return prevProperties;
      }

      const updatedData = prevProperties.map((item) => {
        if (item._id === id) {
          return {
            ...item,
            [field]: value,
            isDirty: JSON.stringify(value) !== JSON.stringify(item[field]),
          };
        }
        return item;
      });
      setHydraulicsParameterData(updatedData);
      setAccordionProperty(updatedData);
      return updatedData;
    });
  };

  const setIsDirtyForId = (targetId) => {
    setProperties(prevData =>
      prevData.map(item =>
        item._id === targetId ? { ...item, isDirty: true } : item
      )
    );
  };

  const getFilteredParameterTypes = (properties = [], currentValue) => {
    const existingParameters = properties.map(item => item.parameter);
    return PARAMETER_ANALYSIS_TYPE.filter(param => !existingParameters.includes(param) || param === currentValue);
  };

  const renderParameterDropDown = (params, properties = []) => {
    const currentValue = params?.value ?? '';
    const filteredData = getFilteredParameterTypes(properties, currentValue);

    return (
      <SelectEditValueTypes
        {...params}
        enableSort={false}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={filteredData}
        value={currentValue}
      />
    );
  };
  const renderValueCell = ({ row, id }) => {
    const [values, setValues] = useState(row?.values?.length ? row?.values : ['', '']);
    const inputRefs = useRef([]);
    const isFirstRow = apiRef?.current?.getRowIndexRelativeToVisibleRows(id) === 0;
    const maxLength = isFirstRow ? 7 : 4;

    useEffect(() => {
      setValues(row?.values?.length ? row.values : ['', '']);
    }, [row.values]);

    const handleChange = (index, newValue) => {
      const updatedValues = [...values];
      updatedValues[index] = newValue === '' ? '' : Number(newValue);
      setValues(updatedValues);
      updateData(row._id, 'values', updatedValues);
    };

    const addInputField = () => {
      if (values.length < maxLength) {
        setValues([...values, '']);
      }
    };

    const removeInputField = () => {
      if (values.length > 2) {
        const updatedValues = values.slice(0, -1);
        setValues(updatedValues);
        updateData(row._id, 'values', updatedValues);
      }
    };

    const handleKeyDown = (event, index) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        const direction = event.shiftKey ? -1 : 1;
        const nextIndex = index + direction;

        if (nextIndex >= 0 && nextIndex < values.length) {
          const nextInput = inputRefs.current[nextIndex];
          if (nextInput) {
            nextInput.focus();
            nextInput.select();
          }
        }
      }
    };

    return (
      <Grid container spacing={1} alignItems="center" justifyContent="center">
        {values.map((val, index) => (
          <Grid item key={index}>
            <TextField
              type="number"
              value={val}
              onChange={(e) => handleChange(index, e.target.value)}
              size="small"
              inputRef={(el) => (inputRefs.current[index] = el)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              inputProps={{
                inputMode: 'decimal',
                pattern: '[0-9]*[.,]?[0-9]*',
                style: {
                  appearance: 'textfield'
                }
              }}
              sx={{
                width: '100px',
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                }
              }}
            />

          </Grid>
        ))}
        <Grid item>
          <IconButton onClick={() => {
            setIsDirtyForId(row?._id);
            addInputField();
          }} disabled={values.length >= maxLength}>
            <AddRounded />
          </IconButton>
          <IconButton onClick={() => {
            setIsDirtyForId(row?._id);
            removeInputField();
          }} disabled={values.length <= 2}>
            <RemoveRounded />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const validateParameterDetails = (details) => {
    const { _id, values, parameter } = details;
    const min_rop = hydraulicsParameter.units.inputs.rop.min;
    const max_rop = hydraulicsParameter.units.inputs.rop.max;
    const min_holeDepth = hydraulicsParameter.units.inputs.hole_depth.min;
    const min_flow_in = hydraulicsParameter.units.inputs.flow_in.min;
    const min_rpm = hydraulicsParameter.units.inputs.rpm.min;
    const max_rpm = hydraulicsParameter.units.inputs.rpm.max;
    const min_mw = hydraulicsParameter.units.inputs.mw.min;
    const max_mw = hydraulicsParameter.units.inputs.mw.max;

    if (new Set(values).size !== values.length) {
      return showError('Please check Value. Should not have duplicate values', _id);
    }

    if (parameter === 'Hole Depth' && values.some(value => value < min_holeDepth)) {
      return showError(HYDRAULICS_RUN_DETAILS.HOLE_DEPTH, _id);
    }
    if (parameter === 'Flow Rate' && values.some(value => value < min_flow_in)) {
      return showError(HYDRAULICS_RUN_DETAILS.FLOW_IN, _id);
    }
    if (parameter === 'RPM' && values.some(value => value < min_rpm || value > max_rpm)) {
      return showError(`RPM should be between ${min_rpm} and ${max_rpm}`, _id);
    }
    if (parameter === 'ROP' && values.some(value => value < min_rop || value > max_rop)) {
      return showError(`ROP should be between ${min_rop} and ${max_rop}`, _id);
    }
    if (parameter === 'MW' && values.some(value => value < min_mw || value > max_mw)) {
      return showError(`Mud Weight should be between ${min_mw} and ${max_mw}`, _id);
    }
    return true;
  };

  const checkHydraulicsParameterValues = (value) => {
    const row = value;
    const invalidFields = [];

    if (row.parameter === '' && !row?.values?.every(val => typeof val === 'number' && !isNaN(val))) {
      invalidFields.push('Please select a parameter and Enter values');
    }

    if (row.parameter === '' && row?.values?.every(val => typeof val === 'number' && !isNaN(val))) {
      invalidFields.push('Please select a parameter');
    }

    if (row.parameter !== '' && !row?.values?.every(val => typeof val === 'number' && !isNaN(val))) {
      invalidFields.push(`Please enter valid values for: ${row?.parameter}`);
    }

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return validateParameterDetails(row);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const saveHydraulicsParameter = async (row) => {
    if (checkHydraulicsParameterValues(row)) {
      const key = Object.keys(PARAMETER_ANALYSIS_TYPE_VALUE).find(
        (k) => PARAMETER_ANALYSIS_TYPE_VALUE[k] === row?.parameter
      ) || row?.parameter;
      const inputs = {
        parameter: key,
        values: unit !== UNIT_SYSTEM.US
          ? row?.values.map((e) =>
            convertUnitSystem(Number(e), hydraulicsParameter?.units?.inputs?.[key]?.conversionFactor)
          )
          : row?.values
      };
      if (row?.isNewRow) {
        const payload = {
          input: inputs,
          wellsInfoId: rowData?.wellsInfoId,
          runId: rowData._id,
        };
        const hydraulicsBody = {
          isParametricResults: rowData?.isParametricResults,
          query: selectedJob?.fromWells
            ? {
                wellsInfoId: selectedJob?.wellsInfoId
              }
            : {
                jobId: selectedJob?._id
              },
        };
        await dispatch(createHydraulicParameter(payload, hydraulicsBody));
        if (inputs?.parameter === 'mw') {
          saveHydraulicDetails();
        }
      } else {
        const payload = {
          input: inputs,
          wellsInfoId: rowData?.wellsInfoId,
          runId: rowData._id,
          _id: row._id
        };
        const hydraulicsBody = {
          isParametricResults: rowData?.isParametricResults,
          query: selectedJob?.fromWells
            ? {
                wellsInfoId: selectedJob?.wellsInfoId
              }
            : {
                jobId: selectedJob?._id
              },
        };
        await dispatch(updateHydraulicParameter(payload, hydraulicsBody));
        if (inputs?.parameter === 'mw') {
          saveHydraulicDetails();
        }
      }
    }
  };

  const removeHydraulicsParameter = async (row) => {
    if (row?.isNewRow) {
      const updatedParams = properties.filter(item => item._id !== row?._id);
      setProperties(updatedParams);
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData?._id
      };
      dispatch(listHydraulicsParameter(payload));
    } else {
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData._id,
        _id: row._id
      };
      const hydraulicsBody = {
        isParametricResults: rowData?.isParametricResults,
        query: selectedJob?.fromWells
          ? {
              wellsInfoId: selectedJob?.wellsInfoId
            }
          : {
              jobId: selectedJob?._id
            },
      };
      await dispatch(deleteHydraulicParameter(payload, hydraulicsBody));
      if (row?.parameter === 'MW') {
        await dispatch(listHydraulicsDetails(rowData));
      }
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const renderActionCell = ({
    row
  }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || hydraulicsParameter.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, hydraulicsParameter?.permissions))}
          onClick={() => {
            saveHydraulicsParameter(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={hydraulicsParameter.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, hydraulicsParameter?.permissions))}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  if (apiRef.current === null) apiRef.current = {};

  return (
    <>
      <Grid container maxWidth='1450px'>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box display="flex" alignItems="center" marginLeft={'13px'}>
            <Typography variant='button' sx={{ cursor: 'pointer' }} fontWeight='700' onClick={handleAddParameter} className={hydraulicsParameter.loading || !checkPermissions(APP_ACTION_TYPES.CREATE, hydraulicsParameter?.permissions) || hydraulicsParameter?.inputs[0]?.isNewRow || properties?.length === MAX_ROW_LIMITS.PARAMETRIC || properties.some(item => item.isDirty === true) || hydraulics?.details?.data[0]?.isNewRow ? classes.disableIcon : ''}>+ Add</Typography>

            <Typography variant='h6' sx={{ marginLeft: '12px' }} color={COLORS.LIGHT_GREY} fontWeight='500'>{` (Note: Maximum ${MAX_ROW_LIMITS.PARAMETRIC} entries are allowed)`}</Typography>

          </Box>
        </Box>
        <Grid item xs={12}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900,
                textAlign: 'justify',
                pointerEvents: proxyLoader ? 'none' : 'auto',
                backgroundColor: proxyLoader ? '#f5f5f5' : 'inherit'
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem !important',
                fontWeight: '900 !important'
              },
            }}
            loading={hydraulicsParameter.loading || hydraulics?.details?.loading}
            columns={parametriTablecColumns(renderParameterDropDown, renderValueCell, renderActionCell)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            hideFooterPagination
            hideFooterSelectedRowCount
            autoHeight={false}
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            height={'25vh'}
            isRowSelectable={() => !proxyLoader}
            getRowClassName={(params) =>
              proxyLoader ? 'Mui-disabled' : (invalidRows[params.id] ? classes.invalidRow : '')
            }
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
            permissions={hydraulicsParameter?.permissions}
          />
        </Grid>
      </Grid>
      {
        deleteAction && <DeleteConfirmationModal
          open={deleteAction}
          handleDelete={removeHydraulicsParameter}
          handleClose={setDeleteAction}
          deleteRow={deleteRow}
        />
      }
    </>
  );
};
ParametricAnalysisModal.propTypes = {
  selectedJob: PropTypes.object,
  rowData: PropTypes.object,
  setHydraulicsParameterData: PropTypes.func,
  proxyLoader: PropTypes.bool,
  setAccordionProperty: PropTypes.func,
  saveHydraulicDetails: PropTypes.func
};
ParametricAnalysisModal.defaultProps = {
  selectedJob: {}
};

export default ParametricAnalysisModal;
